import { ArrowLeft, Check, ChevronsLeft, ChevronsRight, MessageSquareWarning } from "lucide-react"
import * as React from "react"

import type { Message } from "../../data/review-data"
import { DivergencePair } from "../divergence-pair"
import {
  CircleButton,
  FindingContext,
  FindingNumber,
  MessageButton,
  SeverityPill,
} from "../finding-parts"
import type { ReportFindingItem } from "../report-data"

import { FindingThread } from "./finding-thread"

export interface FindingCardProps {
  finding: ReportFindingItem
  /** Which located occurrence the label is framed on. */
  occurrenceIndex: number
  onOccurrenceChange: (index: number) => void
  done: boolean
  /** Omitted for a reader who may not record the client's response. */
  onToggleDone?: (() => void) | undefined
  disputed: boolean
  onToggleDisputed?: (() => void) | undefined
  showMessages: boolean
  onToggleMessages: () => void
  thread: readonly Message[]
  unread: number
  /**
   * Omitted for a reader who may read the conversation but not post to it (a
   * viewer): the thread is shown read-only, with no message box.
   */
  onSend?: ((body: string) => void) | undefined
}

/**
 * The current step: one divergence, and the client's three responses to it.
 *
 * The front is the divergence; the back is its conversation. Both faces are
 * stacked absolutely so the card can turn over, which takes them out of flow —
 * so the card's height follows whichever face is showing.
 */
export function FindingCard({
  finding,
  occurrenceIndex,
  onOccurrenceChange,
  done,
  onToggleDone,
  disputed,
  onToggleDisputed,
  showMessages,
  onToggleMessages,
  thread,
  unread,
  onSend,
}: FindingCardProps): JSX.Element {
  const frontRef = React.useRef<HTMLDivElement>(null)
  const backRef = React.useRef<HTMLDivElement>(null)
  const [faceHeight, setFaceHeight] = React.useState<number | undefined>(undefined)

  const canPost = onSend !== undefined

  React.useLayoutEffect(() => {
    const el = showMessages ? backRef.current : frontRef.current
    if (!el) return
    const measure = (): void => {
      setFaceHeight(el.scrollHeight)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => {
      observer.disconnect()
    }
  }, [showMessages, finding.id])

  const steps = finding.locations.length

  return (
    <div className="[perspective:1600px]">
      <div
        className="relative transition-[transform,height] duration-400 ease-out [transform-style:preserve-3d] motion-reduce:transition-none"
        style={{
          height: faceHeight,
          transform: showMessages ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front — the divergence */}
        <section
          ref={frontRef}
          aria-label={`Finding ${String(finding.number)}`}
          aria-hidden={showMessages}
          inert={showMessages}
          className="absolute inset-x-0 top-0 rounded-lg border border-border bg-surface p-5 shadow-e1 [backface-visibility:hidden]"
        >
          {/* Status bar: which finding, how serious, and what the client can do
              about it. The title gets the full width below instead of sharing a
              row with the actions, where it wrapped to four or five lines. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            <FindingNumber number={finding.number} />
            <SeverityPill severity={finding.severity} />

            {/* `ml-auto`, not a spacer: if a narrow card ever wraps this row, the
                actions stay pinned right rather than dropping to the left. */}
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {onToggleDone && (
                <CircleButton
                  onClick={onToggleDone}
                  pressed={done}
                  label={done ? "Marked reviewed — activate to undo" : "Mark this finding reviewed"}
                  className={
                    done
                      ? "border-master bg-master text-white"
                      : "border-border-strong bg-surface text-text-disabled hover:text-text-3"
                  }
                >
                  <Check className="size-4" strokeWidth={3} aria-hidden="true" />
                </CircleButton>
              )}
              {onToggleDisputed && (
                <CircleButton
                  onClick={onToggleDisputed}
                  pressed={disputed}
                  label={
                    disputed
                      ? "Disputed — activate to withdraw the dispute"
                      : "Dispute this finding and tell the review team why"
                  }
                  className={
                    disputed
                      ? "border-info bg-info text-white"
                      : "border-border-strong bg-surface text-text-3 hover:text-text-2"
                  }
                >
                  <MessageSquareWarning className="size-4" aria-hidden="true" />
                </CircleButton>
              )}
              <MessageButton onClick={onToggleMessages} canPost={canPost} unread={unread} />
            </div>
          </div>

          <h2 className="mt-3 text-heading text-text">{finding.title}</h2>

          <FindingContext finding={finding} />

          {disputed && (
            <p className="mt-4 rounded-sm border border-info/30 bg-info-tint p-2.5 text-caption text-info-strong">
              Marked as disputed. Explain why in the conversation; the review team decides whether
              the finding stands.
            </p>
          )}

          <OccurrenceStepper
            count={finding.occurrenceCount}
            steps={steps}
            index={occurrenceIndex}
            onChange={onOccurrenceChange}
          />
          {finding.occurrenceCount > 1 && (
            <p className="mt-1.5 text-[11px] leading-relaxed text-text-3">
              This text recurs {finding.occurrenceCount} times on the label and must be corrected at
              every occurrence.
              {steps > 1 ? " Step through them to see each location." : ""}
            </p>
          )}

          <DivergencePair
            className="mt-4"
            stacked
            printedText={finding.printedText}
            masterText={finding.masterText}
            fallback={finding.divergence}
          />

          {finding.ruleCheck && (
            <p className="mt-3 text-body-sm text-text-2">What we check: {finding.ruleCheck}</p>
          )}
          {finding.remediation && (
            <div className="mt-3 rounded-sm border border-master/30 bg-master-tint p-2.5 text-caption">
              <span className="block font-semibold text-master">What to change</span>
              <span className="break-words text-text-2">{finding.remediation}</span>
            </div>
          )}
          {finding.citations.length > 0 && (
            <p className="mt-3 text-[11px] leading-relaxed text-text-3">
              Authority: {finding.citations.join("; ")}
            </p>
          )}
        </section>

        {/* Back — the conversation about this divergence */}
        <section
          ref={backRef}
          aria-label={`Messages about finding ${String(finding.number)}`}
          aria-hidden={!showMessages}
          inert={!showMessages}
          className="absolute inset-x-0 top-0 flex flex-col rounded-lg border border-border bg-surface p-4 shadow-e1 [backface-visibility:hidden] [transform:rotateY(180deg)]"
        >
          <div className="flex items-center gap-2 border-b border-border-soft pb-3">
            {/* Icon only, matching the card's other round actions; the name and
                tooltip still say where it goes. */}
            <CircleButton
              onClick={onToggleMessages}
              label="Back to the finding"
              className="border border-border bg-surface text-text-3 hover:text-text"
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
            </CircleButton>
            <span className="min-w-0 flex-1 truncate text-body-sm font-medium text-text-2">
              {finding.number}. {finding.title}
            </span>
          </div>
          <div className="flex h-[420px] min-h-0 flex-col">
            <FindingThread messages={thread} onSend={onSend} focusComposer={showMessages} />
          </div>
        </section>
      </div>
    </div>
  )
}

/**
 * How many places carry this divergence, and — when more than one is located —
 * which of them the label is framed on.
 */
function OccurrenceStepper({
  count,
  steps,
  index,
  onChange,
}: {
  /** Occurrences on the label, located or not. */
  count: number
  /** Occurrences with a verified location, which are what can be stepped to. */
  steps: number
  index: number
  onChange: (index: number) => void
}): JSX.Element {
  if (steps <= 1) {
    return (
      <div className="mt-4 flex items-center justify-between rounded-md bg-surface-sunken px-3 py-2">
        <span className="text-caption font-medium text-text-3">
          {count > 1 ? "Occurrences on label" : "Single occurrence"}
        </span>
        <span className="text-body-sm font-semibold tabular-nums text-text-2">{count}</span>
      </div>
    )
  }
  return (
    <div className="mt-4 flex items-center justify-between rounded-md bg-surface-sunken px-3 py-1.5">
      <span className="text-caption font-medium text-text-3">Occurrence on label</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => {
            onChange(index - 1)
          }}
          disabled={index <= 0}
          aria-label="Previous occurrence"
          className="grid size-7 place-items-center rounded-md text-text-3 hover:bg-surface hover:text-text disabled:opacity-40"
        >
          <ChevronsLeft className="size-4" aria-hidden="true" />
        </button>
        <span
          aria-live="polite"
          className="min-w-12 text-center text-body-sm font-semibold tabular-nums text-text-2"
        >
          {index + 1} / {steps}
        </span>
        <button
          type="button"
          onClick={() => {
            onChange(index + 1)
          }}
          disabled={index >= steps - 1}
          aria-label="Next occurrence"
          className="grid size-7 place-items-center rounded-md text-text-3 hover:bg-surface hover:text-text disabled:opacity-40"
        >
          <ChevronsRight className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
